import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import WorkflowGuide from '../../components/olymp/WorkflowGuide';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/authContext';
import { useAppearance } from '../../context/AppearanceContext';
import DashboardOnboardingDialog from '../../components/olymp/DashboardOnboardingDialog';
import { getParameterLookupId } from '../../services/catalogService';

// Import JSON data
import parametersData from '../../data/parameters.json';
import parameterListsData from '../../data/parameterLists.json';
import cohortsData from '../../data/cohorts.json';
import recentActivityData from '../../data/recentActivity.json';
import notificationsData from '../../data/notifications.json';

// Helper function to format time ago
const getTimeAgo = (timestamp, t) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now - activityTime;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return t('dashboard.timeAgo.fewMinutes');
  if (diffHours < 24) return t('dashboard.timeAgo.hours', { count: diffHours });
  if (diffDays === 1) return t('dashboard.timeAgo.oneDay');
  return t('dashboard.timeAgo.days', { count: diffDays });
};

const getOnboardingStorageKey = (currentUser) => {
  const userKey = currentUser?.id || currentUser?.email || 'unknown-user';
  return `olymp-dashboard-onboarding-seen-v1:${userKey}`;
};

const OlympDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { appearance } = useAppearance();
  const widgets = appearance.dashboardWidgets ?? {};
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pt')
    ? 'pt-BR'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : 'en-US';
  const currentUserName = user?.name || user?.email || '';
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

  // Load data from JSON files and localStorage
  const [favorites, setFavorites] = useState([]);
  const [myLists, setMyLists] = useState([]);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  // Load favorites from localStorage
  useEffect(() => {
    const storedFavorites = JSON.parse(localStorage.getItem('olymp_favorites') || '[]');
    const favoriteParams = parametersData.parameters.filter(p =>
      storedFavorites.includes(p.id)
    );
    setFavorites(favoriteParams);

    // Load user's lists (filter by current user or public)
    const userLists = parameterListsData.lists.filter(list =>
      (currentUserName && list.createdBy === currentUserName) || list.isPublic
    );
    setMyLists(userLists.slice(0, 3)); // Show only first 3
  }, [currentUserName]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setIsOnboardingOpen(false);
      return;
    }

    const storageKey = getOnboardingStorageKey(user);
    const hasSeenOnboarding = localStorage.getItem(storageKey) === '1';
    setIsOnboardingOpen(!hasSeenOnboarding);
  }, [authLoading, user]);

  const dismissOnboarding = () => {
    if (user) {
      localStorage.setItem(getOnboardingStorageKey(user), '1');
    }
    setIsOnboardingOpen(false);
  };

  // Calculate stats from real data
  const stats = {
    totalParameters: parametersData.parameters.length,
    omopMapped: parametersData.parameters.filter(p =>
      p.omopMapping?.status === 'validated' || p.omopMapping?.status === 'mapped'
    ).length,
    pendingMappings: parametersData.parameters.filter(p =>
      p.omopMapping?.status === 'pending' || !p.omopMapping
    ).length,
    favoritesCount: favorites.length,
    myLists: parameterListsData.lists.length,
    myCohorts: cohortsData.cohorts.length,
    myRequests: cohortsData.cohorts.length // Anzahl der Anfragen basierend auf Kohorten
  };

  // Get recent activity from JSON
  const recentActivity = recentActivityData.activities.slice(0, 4).map(activity => ({
    ...activity,
    time: getTimeAgo(activity.timestamp, t)
  }));

  // Get notifications from JSON
  const notifications = notificationsData.notifications.slice(0, 3);

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <DashboardOnboardingDialog
          open={isOnboardingOpen}
          onDismiss={dismissOnboarding}
        />

        {/* Welcome Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            mb: 3
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('dashboard.hero.title')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {t('dashboard.hero.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/catalog')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5
              }}
            >
              {t('dashboard.hero.catalogCta')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate('/cohorts')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              {t('dashboard.hero.cohortsCta')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/requests')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              {t('dashboard.hero.requestsCta')}
            </Button>
          </Box>
        </Paper>

        <WorkflowGuide />

        {/* Stats Cards */}
        {widgets.statistics !== false && <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.parametersInCatalog')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.totalParameters)}
                    </Typography>
                  </Box>
                  <FolderIcon sx={{ fontSize: 50, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.omopMapped')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.omopMapped)}
                    </Typography>
                    <Typography variant="caption" color="success.main" fontWeight="600">
                      {t('dashboard.stats.coverage', { value: Math.round((stats.omopMapped / stats.totalParameters) * 100) })}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 50, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.myFavorites')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.favoritesCount)}
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: 50, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/requests')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.dataRequests')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.myRequests)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.stats.cohortsCount', { count: formatNumber(stats.myCohorts) })}
                    </Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 50, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>}

        {/* Content Grid */}
        <Grid container spacing={3}>
          {/* Favorites Widget */}
          {widgets.incomingRequests !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    <StarIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                    {t('dashboard.widgets.favorites')}
                  </Typography>
                  {favorites.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      {t('dashboard.widgets.viewAll')}
                    </Button>
                  )}
                </Box>
                {favorites.length > 0 ? (
                  <List dense>
                    {favorites.slice(0, 5).map(fav => (
                      <ListItem
                        key={fav.id}
                        button
                        onClick={() => navigate(`/parameter/${getParameterLookupId(fav)}`)}
                        secondaryAction={
                          <Chip
                            label={fav.omopMapping?.status === 'validated' || fav.omopMapping?.status === 'mapped' ? 'OMOP' : t('dashboard.widgets.pending')}
                            size="small"
                            color={fav.omopMapping?.status === 'validated' || fav.omopMapping?.status === 'mapped' ? 'success' : 'default'}
                          />
                        }
                      >
                        <ListItemIcon>
                          <StarIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={fav.name}
                          secondary={fav.category}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('dashboard.widgets.noFavorites')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t('dashboard.widgets.noFavoritesHint')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      {t('dashboard.widgets.goToCatalog')}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>}

          {/* Recent Activity */}
          {widgets.activityLog !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('dashboard.widgets.recentActivity')}
                </Typography>
                <List dense>
                  {recentActivity.map(activity => (
                    <ListItem key={activity.id}>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}

          {/* My Lists */}
          {widgets.runningAnalyses !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('dashboard.widgets.myLists')}
                  </Typography>
                  <IconButton size="small" color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
                <List dense>
                  {myLists.map(list => (
                    <ListItem
                      key={list.id}
                      button
                      onClick={() => navigate('/catalog')}
                      secondaryAction={
                        <Typography variant="body2" color="textSecondary">
                          {t('dashboard.widgets.parameterCount', { count: formatNumber(list.parameterIds.length) })}
                        </Typography>
                      }
                    >
                      <ListItemIcon>
                        <FolderIcon color={list.isPublic ? 'primary' : 'action'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={list.name}
                        secondary={list.isPublic ? t('dashboard.widgets.public') : t('dashboard.widgets.private')}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}

          {/* Notifications */}
          {widgets.paymentOverview !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  <NotificationsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {t('dashboard.widgets.notifications')}
                  {notifications.filter(n => n.unread).length > 0 && (
                    <Chip
                      label={formatNumber(notifications.filter(n => n.unread).length)}
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <List dense>
                  {notifications.map(notif => (
                    <ListItem
                      key={notif.id}
                      sx={{
                        backgroundColor: notif.unread ? 'action.hover' : 'transparent',
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemText
                        primary={notif.text}
                        primaryTypographyProps={{
                          fontWeight: notif.unread ? 600 : 400
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OlympDashboard;
